import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Award,
  ChevronRight,
  Users,
  Copy,
  Sparkles,
  Check,
  X,
  BarChart3,
  Info
} from 'lucide-react';
import Pagination from '@mui/material/Pagination';
import patientService from '../../services/PatientService';
import { AreaChart } from '../../components/charts/CustomCharts';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer
} from 'recharts';
import '../styles/PatientAnalyticsView.css';

const PatientAnalyticsView = ({ selectedPatientId, patients }) => {
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [dateRange, setDateRange] = useState('7d');
  const [viewMode, setViewMode] = useState('sessions'); // 'sessions' or 'daily'
  const [analytics, setAnalytics] = useState(null);
  const [previousPeriodAnalytics, setPreviousPeriodAnalytics] = useState(null);
  const [comparisonMetadata, setComparisonMetadata] = useState({ canCompare: false, reason: null });
  const [platformSessions, setPlatformSessions] = useState([]);
  const [dailySummaries, setDailySummaries] = useState([]);
  const [gamePerformance, setGamePerformance] = useState({});
  const [loading, setLoading] = useState(false);
  const [isPlaceholderData, setIsPlaceholderData] = useState(false);
  const [showInsightsModal, setShowInsightsModal] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [sessionsPage, setSessionsPage] = useState(1); // Pagination for sessions

  // Update selected patient when prop changes
  useEffect(() => {
    console.log('📊 PatientAnalyticsView - selectedPatientId:', selectedPatientId, 'patients:', patients);
    if (selectedPatientId && patients) {
      const patient = patients.find(p => p.id === selectedPatientId || p.id === Number(selectedPatientId));
      console.log('📊 Found patient:', patient);
      setSelectedPatient(patient);
    } else {
      setSelectedPatient(null);
    }
  }, [selectedPatientId, patients]);

  // Load analytics data when patient or date range changes
  useEffect(() => {
    if (selectedPatient) {
      loadAnalyticsData();
      setSessionsPage(1); // Reset to first page when data changes
    }
  }, [selectedPatient, dateRange]);

  const loadAnalyticsData = async () => {
    if (!selectedPatient) {
      console.log('📊 No patient selected, skipping data load');
      return;
    }

    console.log('📊 Loading analytics for patient:', selectedPatient);
    setLoading(true);
    try {
      // Load all analytics data in parallel
      let [comparisonData, sessions, gamePerf] = await Promise.all([
        patientService.getPatientAnalyticsWithComparison(selectedPatient.id, dateRange),
        patientService.getPatientSessions(selectedPatient.id, dateRange),
        patientService.getGamePerformance(selectedPatient.id)
      ]);

      // Extract current and previous period data
      let analyticsData = comparisonData.current;
      let previousAnalyticsData = comparisonData.previous;

      console.log('📊 Analytics data received:', {
        analyticsData,
        previousAnalyticsData,
        canCompare: comparisonData.canCompare,
        reason: comparisonData.reason,
        analyticsDataSessions: analyticsData?.sessions,
        analyticsDataLength: analyticsData?.sessions?.length,
        sessions,
        gamePerf,
        hasAnalyticsSessions: analyticsData?.sessions?.length
      });

      // Use placeholder data if no real data exists
      let usingPlaceholder = false;
      console.log('📊 Checking if we need placeholder:', {
        hasAnalyticsData: !!analyticsData,
        hasSessions: !!analyticsData?.sessions,
        sessionLength: analyticsData?.sessions?.length,
        condition: !analyticsData || !analyticsData.sessions || analyticsData.sessions.length === 0
      });
      if (!analyticsData || !analyticsData.sessions || analyticsData.sessions.length === 0) {
        console.log('📊 Using placeholder data for analytics');
        usingPlaceholder = true;
        analyticsData = {
          sessions: [
            {
              id: 1,
              game_name: 'Apple Pick',
              start_time: new Date('2024-01-15T10:30:00').toISOString(),
              duration_seconds: 300,
              reps_completed: 15,
              form_score: 88,
              rom_display: 'Full range',
              rom_percentage: 90,
              accuracy_score: 92,
              control_score: 85
            },
            {
              id: 2,
              game_name: 'Super Punch',
              start_time: new Date('2024-01-13T14:15:00').toISOString(),
              duration_seconds: 420,
              reps_completed: 20,
              form_score: 85,
              rom_display: '¾ range',
              rom_percentage: 75,
              accuracy_score: 87,
              control_score: 83
            },
            {
              id: 3,
              game_name: 'Hot Potato',
              start_time: new Date('2024-01-11T09:45:00').toISOString(),
              duration_seconds: 360,
              reps_completed: 18,
              form_score: 91,
              rom_display: 'Full range',
              rom_percentage: 95,
              accuracy_score: 93,
              control_score: 89
            },
            {
              id: 4,
              game_name: 'Ocean Row',
              start_time: new Date('2024-01-09T11:00:00').toISOString(),
              duration_seconds: 480,
              reps_completed: 25,
              form_score: 83,
              rom_display: '½ range',
              rom_percentage: 50,
              accuracy_score: 85,
              control_score: 80
            },
            {
              id: 5,
              game_name: 'Berry Harvest',
              start_time: new Date('2024-01-07T15:20:00').toISOString(),
              duration_seconds: 240,
              reps_completed: 12,
              form_score: 90,
              rom_display: 'Full range',
              rom_percentage: 92,
              accuracy_score: 91,
              control_score: 88
            }
          ],
          summary: {
            total_sessions: 5,
            total_reps: 90,
            avg_form_score: 87.4,
            avg_rom_percentage: 80.4
          }
        };
      }

      if (!sessions || sessions.length === 0) {
        console.log('📊 Using placeholder platform sessions');
        sessions = [
          {
            start_time: new Date(Date.now() - 86400000).toISOString(),
            end_time: new Date(Date.now() - 84600000).toISOString(),
            total_duration_seconds: 1800,
            games_played: 2,
            total_reps: 35,
            avg_form_score: 86.5
          }
        ];
      }

      if (!gamePerf || Object.keys(gamePerf).length === 0) {
        console.log('📊 Using placeholder game performance data');
        gamePerf = {
          'Apple Pick': { sessions: 1, avgFormScore: 88, totalReps: 15, completions: 1 },
          'Super Punch': { sessions: 1, avgFormScore: 85, totalReps: 20, completions: 1 },
          'Hot Potato': { sessions: 1, avgFormScore: 91, totalReps: 18, completions: 1 },
          'Tomato Dodge': { sessions: 1, avgFormScore: 87, totalReps: 22, completions: 1 },
          'Ocean Row': { sessions: 1, avgFormScore: 83, totalReps: 25, completions: 1 },
          'Sky Flight': { sessions: 1, avgFormScore: 86, totalReps: 16, completions: 1 },
          'Plant Care': { sessions: 1, avgFormScore: 89, totalReps: 14, completions: 1 },
          'Berry Harvest': { sessions: 1, avgFormScore: 90, totalReps: 12, completions: 1 }
        };
      }

      setAnalytics(analyticsData);
      setPreviousPeriodAnalytics(previousAnalyticsData);
      setComparisonMetadata({
        canCompare: comparisonData.canCompare && !usingPlaceholder,
        reason: comparisonData.reason
      });
      setPlatformSessions(sessions);
      setGamePerformance(gamePerf);
      setIsPlaceholderData(usingPlaceholder);

      // Compute daily summaries from actual session data
      let dailyData = [];
      
      if (analyticsData && analyticsData.sessions && analyticsData.sessions.length > 0) {
        console.log('📊 Computing daily summaries from real session data');
        
        // Group sessions by date
        const sessionsByDate = {};
        analyticsData.sessions.forEach(session => {
          const date = new Date(session.start_time).toISOString().split('T')[0];
          if (!sessionsByDate[date]) {
            sessionsByDate[date] = [];
          }
          sessionsByDate[date].push(session);
        });

        // Calculate daily aggregates
        dailyData = Object.keys(sessionsByDate).map(date => {
          const daySessions = sessionsByDate[date];
          const totalReps = daySessions.reduce((sum, s) => sum + (s.reps_completed || 0), 0);
          const totalDuration = daySessions.reduce((sum, s) => sum + (s.duration_seconds || 0), 0);
          const avgFormScore = daySessions.reduce((sum, s) => sum + (s.form_score || 0), 0) / daySessions.length;
          const avgRomPercentage = daySessions.reduce((sum, s) => sum + (s.rom_percentage || 0), 0) / daySessions.length;
          
          // Categorize reps by body part (basic mapping from game names)
          const upperBodyGames = ['Apple Pick', 'Super Punch', 'Ocean Row', 'Sky Flight', 'Plant Care', 'Berry Picking'];
          const coreGames = ['Hot Potato', 'Tomato Dodge'];
          const lowerBodyGames = ['Soccer Kick', 'Ice Skate'];
          
          const upperBodyReps = daySessions
            .filter(s => upperBodyGames.includes(s.game_name))
            .reduce((sum, s) => sum + (s.reps_completed || 0), 0);
          const coreReps = daySessions
            .filter(s => coreGames.includes(s.game_name))
            .reduce((sum, s) => sum + (s.reps_completed || 0), 0);
          const lowerBodyReps = daySessions
            .filter(s => lowerBodyGames.includes(s.game_name))
            .reduce((sum, s) => sum + (s.reps_completed || 0), 0);

          return {
            id: date,
            summary_date: date,
            session_count: daySessions.length,
            total_platform_time_seconds: totalDuration,
            total_games_played: daySessions.length,
            total_reps: totalReps,
            avg_form_score: Math.round(avgFormScore),
            avg_rom_percentage: Math.round(avgRomPercentage),
            upper_body_reps: upperBodyReps,
            core_reps: coreReps,
            lower_body_reps: lowerBodyReps
          };
        });

        // Sort by date descending (most recent first)
        dailyData.sort((a, b) => new Date(b.summary_date) - new Date(a.summary_date));
      } else if (!usingPlaceholder) {
        console.log('📊 No session data available for daily summaries');
        dailyData = [];
      } else {
        // Only use placeholder if we're already using placeholder session data
        console.log('📊 Using placeholder daily summaries');
        const days = dateRange === '24h' ? 1 : dateRange === '7d' ? 7 : 30;
        dailyData = [];
        for (let i = 0; i < Math.min(days, 7); i++) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          dailyData.push({
            id: i + 1,
            summary_date: date.toISOString().split('T')[0],
            session_count: Math.floor(Math.random() * 3) + 1,
            total_platform_time_seconds: 1800 + Math.floor(Math.random() * 1800),
            total_games_played: Math.floor(Math.random() * 4) + 2,
            total_reps: 15 + Math.floor(Math.random() * 20),
            avg_form_score: 80 + Math.floor(Math.random() * 15),
            avg_rom_percentage: 70 + Math.floor(Math.random() * 25),
            upper_body_reps: Math.floor(Math.random() * 15) + 5,
            core_reps: Math.floor(Math.random() * 10) + 3,
            lower_body_reps: 0
          });
        }
        dailyData.reverse();
      }

      setDailySummaries(dailyData);

    } catch (error) {
      console.error('Error loading analytics:', error);
      // Still show placeholder data on error
      setAnalytics({
        sessions: [],
        summary: {}
      });
      setPlatformSessions([]);
      setDailySummaries([]);
      setGamePerformance({});
      setIsPlaceholderData(true);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return '0s';
    if (seconds < 60) {
      return `${seconds}s`;
    }
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    if (minutes > 0 && secs > 0) {
      return `${minutes}m ${secs}s`;
    }
    return `${minutes}m`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Helper: Check if trend display needs "vs last period" suffix
   * Special messages (emojis, edge cases) don't need the suffix
   */
  const needsVsLastPeriod = (trendDisplay) => {
    if (!trendDisplay) return true;
    // Special messages that don't need "vs last period"
    const specialPrefixes = ['🎉', '↑ New', '↓ New', '— No', '—'];
    return !specialPrefixes.some(prefix => trendDisplay.startsWith(prefix));
  };

  /**
   * Calculate smart trend with edge case handling
   * Note: Check specific edge cases BEFORE checking canCompare to show special messages
   */
  const calculateSmartTrend = (current, previous, metadata) => {
    // Edge case: First ever session (check BEFORE canCompare)
    if (previous === 0 && current === 1) {
      return { display: '🎉 First session', trend: 100, direction: 'up', message: 'First activity' };
    }

    // Edge case: New activity after inactivity (check BEFORE canCompare)
    if (previous === 0 && current > 0) {
      return { display: '↑ New activity', trend: 100, direction: 'up', message: 'Resumed after break' };
    }

    // Edge case: Both periods empty
    if (previous === 0 && current === 0) {
      return { display: '— No activity', trend: 0, direction: 'neutral', message: 'No sessions recorded' };
    }

    // Edge case: Became inactive
    if (previous > 0 && current === 0) {
      return { display: '↓ -100%', trend: -100, direction: 'down', message: 'No activity this period' };
    }

    // Edge case: Can't compare (new user or insufficient history)
    // This comes AFTER specific edge cases so they can still show special messages
    if (!metadata.canCompare) {
      if (metadata.reason === 'insufficient_history') {
        return { display: '— No comparison', trend: 0, direction: 'neutral', message: 'Insufficient history' };
      }
      return { display: '—', trend: 0, direction: 'neutral', message: 'No comparison available' };
    }

    // Normal calculation (when we have valid comparison data)
    const percentChange = ((current - previous) / previous * 100);
    const formattedPercent = percentChange.toFixed(1);
    const display = percentChange > 0 ? `+${formattedPercent}%` : `${formattedPercent}%`;
    const direction = percentChange > 0 ? 'up' : percentChange < 0 ? 'down' : 'stable';

    return { display, trend: percentChange, direction, message: null };
  };

  // Calculate aggregated metrics with real period comparison
  const getTotalMetrics = () => {
    if (!analytics || !analytics.summary) return {};

    // Current period metrics
    const currentSessions = analytics.summary.total_sessions || 0;
    const currentTotalTime = analytics.summary.total_time || 0;
    const currentTotalReps = analytics.summary.total_reps || 0;
    const currentAvgFormScore = analytics.summary.avg_form_score || 0;
    const currentAvgRom = analytics.summary.avg_rom_percentage || 0;
    
    // Previous period metrics
    const previousSessions = previousPeriodAnalytics?.summary?.total_sessions || 0;
    const previousTotalTime = previousPeriodAnalytics?.summary?.total_time || 0;
    const previousTotalReps = previousPeriodAnalytics?.summary?.total_reps || 0;
    const previousAvgFormScore = previousPeriodAnalytics?.summary?.avg_form_score || 0;
    const previousAvgRom = previousPeriodAnalytics?.summary?.avg_rom_percentage || 0;
    
    // Platform time = total login session time (from user_sessions)
    const platformTime = platformSessions.reduce((sum, s) =>
      sum + (s.session_duration_seconds || 0), 0);
    
    // Game time = actual time in games
    const gameTime = analytics.sessions.reduce((sum, s) =>
      sum + (s.duration_seconds || 0), 0);

    // Calculate real trends for each metric
    const sessionsTrend = calculateSmartTrend(currentSessions, previousSessions, comparisonMetadata);
    const timeTrend = calculateSmartTrend(currentTotalTime, previousTotalTime, comparisonMetadata);
    const repsTrend = calculateSmartTrend(currentTotalReps, previousTotalReps, comparisonMetadata);
    const formScoreTrend = calculateSmartTrend(currentAvgFormScore, previousAvgFormScore, comparisonMetadata);
    const romTrend = calculateSmartTrend(currentAvgRom, previousAvgRom, comparisonMetadata);

    console.log('📊 Calculated trends:', {
      sessions: { current: currentSessions, previous: previousSessions, trend: sessionsTrend },
      time: { current: currentTotalTime, previous: previousTotalTime, trend: timeTrend },
      reps: { current: currentTotalReps, previous: previousTotalReps, trend: repsTrend },
      formScore: { current: currentAvgFormScore, previous: previousAvgFormScore, trend: formScoreTrend },
      rom: { current: currentAvgRom, previous: previousAvgRom, trend: romTrend },
      canCompare: comparisonMetadata.canCompare
    });

    return {
      totalSessions: currentSessions,
      totalTime: currentTotalTime,
      totalReps: currentTotalReps,
      avgFormScore: currentAvgFormScore,
      avgRom: currentAvgRom,
      platformTime,
      gameTime,
      engagementRate: platformTime > 0 ? ((gameTime / platformTime) * 100).toFixed(1) : 0,
      
      // Trend data for each metric
      sessionsTrend,
      timeTrend,
      repsTrend,
      formScoreTrend,
      romTrend
    };
  };

  const metrics = getTotalMetrics();

  console.log('📊 Final metrics:', metrics);

  // Copy insights to clipboard
  const handleCopyInsights = () => {
    const insightsText = `
MENDY'S WEEKLY INSIGHTS
Patient: ${selectedPatient?.firstName || selectedPatient?.firstname || 'Patient'} ${selectedPatient?.lastInitial || selectedPatient?.lastinitial || ''}
Date: ${new Date().toLocaleDateString()}

OVERVIEW
Patient progressed well this week. Patient engaged consistently in supplemental exercises, averaging 2 minutes more per session compared to the prior week. Notably, patient achieved a new personal best in overhead reach (Apple Picking), completing 45 repetitions in 3 minutes.

STRENGTHS
• Daily engagement maintained throughout the week
• Increased total exercise time (+10 minutes compared to last week)
• Strong performance in upper body games, especially overhead reach

AREAS TO WATCH
• Average session duration is still below the assigned 20-minute target
• Range of motion in upper extremity exercises shows improvement but remains variable
• Sessions occurred at different times of day, which may affect consistency

RECOMMENDATIONS
(These suggestions are based solely on objective exercise data. Intended for clinician review. Please apply your clinical judgment to determine appropriateness.)
• Consider upgrading program by adding a new game or increasing duration/frequency
• Encourage consistent timing for sessions to support habit formation
    `.trim();

    navigator.clipboard.writeText(insightsText);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };


  if (!selectedPatient) {
    return (
      <Card className="p-12">
        <div className="text-center">
          <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Patient Selected</h3>
          <p className="text-gray-600">
            Please select a patient from the dropdown above to view their analytics
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Placeholder Data Notice */}
      {isPlaceholderData && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 shadow-md">
          <div className="flex items-start gap-3">
            <div className="text-amber-600 text-2xl mt-1">⚠️</div>
            <div className="flex-1">
              <p className="text-base font-bold text-amber-900 mb-1">Demo Data Only - No Real Sessions Found</p>
              <p className="text-sm text-amber-800 mb-3">
                The data shown below is sample data for demonstration purposes.
              </p>
              <div className="bg-white/70 border border-amber-200 rounded p-3 text-xs text-amber-900">
                <p className="font-semibold mb-2">📊 To see real analytics:</p>
                <ol className="list-decimal list-inside space-y-1 ml-2">
                  <li>Have the patient log in with their access code</li>
                  <li>Complete at least one exercise game</li>
                  <li>Return to this screen to view actual performance data</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Patient Header - Two Cards Side by Side */}
      <div className="flex gap-4" style={{ display: 'flex !important', flexDirection: 'row !important', gap: '1rem', flexWrap: 'nowrap' }}>
        {/* Patient Info Card */}
        <Card className="w-1/2" style={{ width: '50%', minWidth: '300px' }}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-12 w-12 rounded-full bg-[#6d4f79]/10 flex items-center justify-center">
                    <span className="text-xl font-bold text-[#6d4f79]">
                      {(selectedPatient.firstName || selectedPatient.firstname || 'P')[0]}
                    </span>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      {selectedPatient.firstName || selectedPatient.firstname} {selectedPatient.lastInitial || selectedPatient.lastinitial}
                    </h2>
                    <p className="text-base text-gray-600">
                      Room {selectedPatient.roomNumber || selectedPatient.roomnumber}
                      {(selectedPatient.bedNumber || selectedPatient.bednumber) && `, Bed ${selectedPatient.bedNumber || selectedPatient.bednumber}`}
                    </p>
                  </div>
                </div>
                
                {/* Styled Dropdown */}
                <div className="relative">
                  <select
                    className="w-full px-4 py-3 pr-10 border border-gray-200 rounded-lg bg-gray-50 hover:bg-white transition-colors text-base font-medium text-gray-700 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#6d4f79]/20 focus:border-[#6d4f79]"
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value)}
                  >
                    <option value="24h">Last 24 Hours</option>
                    <option value="7d">Last 7 Days</option>
                    <option value="30d">Last 30 Days</option>
                    <option value="all">All Time</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Mendy's Weekly Insights Card with Subtle Glow */}
        <div 
          className="w-1/2 cursor-pointer transition-all duration-300 relative overflow-hidden group"
          style={{
            width: '50%',
            minWidth: '300px',
            borderStyle: 'solid',
            borderColor: 'rgba(109, 79, 121, 0.3)',
            borderWidth: '2px',
            borderRadius: '8px',
            animation: 'subtleGlow 3s ease-in-out infinite',
            background: 'linear-gradient(to right, #ffffff, #fafafa)'
          }}
          onClick={() => setShowInsightsModal(true)}
        >
          <div className="p-4 relative flex items-center h-full">
            <div className="flex items-center gap-4 w-full">
              <div className="relative flex-shrink-0">
                <div className="absolute inset-0 bg-[#6d4f79]/10 rounded-full blur-xl animate-pulse scale-150" />
                <img
                  src="/auth/logo.svg"
                  alt="MoveMend"
                  className="h-20 w-20 relative"
                  style={{ width: '80px', height: '80px' }}
                />
              </div>
              <div className="flex-1 flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-0.5">
                  <h3 className="text-lg font-bold text-gray-900 leading-tight">
                    Mendy's Weekly Insights
                  </h3>
                  <Sparkles className="h-4 w-4 text-[#6d4f79] animate-pulse" />
                </div>
                <p className="text-base text-gray-600 leading-tight">
                  New insights available • Click to view
                </p>
              </div>
              <svg className="w-6 h-6 text-gray-400 group-hover:text-[#6d4f79] transition-colors flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              Games Played
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.totalSessions}</div>
            <p className={`text-xs ${
              metrics.sessionsTrend?.direction === 'up' ? 'text-green-600' :
              metrics.sessionsTrend?.direction === 'down' ? 'text-red-600' : 'text-gray-500'
            }`}>
              {dateRange === 'all' 
                ? 'Total sessions' 
                : `${metrics.sessionsTrend?.display || '—'}${needsVsLastPeriod(metrics.sessionsTrend?.display) ? ' vs last period' : ''}`
              }
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              Exercise Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatDuration(metrics.totalTime)}</div>
            <p className={`text-xs ${
              metrics.timeTrend?.direction === 'up' ? 'text-green-600' :
              metrics.timeTrend?.direction === 'down' ? 'text-red-600' : 'text-gray-500'
            }`}>
              {dateRange === 'all' 
                ? 'Total exercise time' 
                : `${metrics.timeTrend?.display || '—'}${needsVsLastPeriod(metrics.timeTrend?.display) ? ' vs last period' : ''}`
              }
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              Avg Form Score
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {metrics.avgFormScore?.toFixed(1) || 0}%
            </div>
            <p className={`text-xs ${
              metrics.formScoreTrend?.direction === 'up' ? 'text-green-600' :
              metrics.formScoreTrend?.direction === 'down' ? 'text-red-600' : 'text-gray-500'
            }`}>
              {dateRange === 'all' 
                ? 'Overall average' 
                : `${metrics.formScoreTrend?.display || '—'}${needsVsLastPeriod(metrics.formScoreTrend?.display) ? ' vs last period' : ''}`
              }
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
              Avg ROM
              <div className="relative group">
                <Info className="h-4 w-4 text-gray-400 cursor-help" />
                <div className="absolute left-0 top-6 w-48 p-3 bg-white border-2 border-gray-300 text-gray-900 text-xs rounded shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                  <div className="font-bold mb-1.5 text-gray-800">ROM Ranges:</div>
                  <div className="text-gray-700">Full range: ≥90%</div>
                  <div className="text-gray-700">¾ range: 67-89%</div>
                  <div className="text-gray-700">½ range: 45-66%</div>
                  <div className="text-gray-700">¼ range: 22-44%</div>
                  <div className="text-gray-700">Limited: &lt;22%</div>
                </div>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {metrics.avgRom >= 90 ? 'Full range' :
               metrics.avgRom >= 67 ? '¾ range' :
               metrics.avgRom >= 45 ? '½ range' :
               metrics.avgRom >= 22 ? '¼ range' : 'Limited'}
            </div>
            <p className={`text-xs ${
              metrics.romTrend?.direction === 'up' ? 'text-green-600' :
              metrics.romTrend?.direction === 'down' ? 'text-red-600' : 'text-gray-500'
            }`}>
              {dateRange === 'all' 
                ? 'Overall average' 
                : `${metrics.romTrend?.display || '—'}${needsVsLastPeriod(metrics.romTrend?.display) ? ' vs last period' : ''}`
              }
            </p>
          </CardContent>
        </Card>
      </div>

      {/* View Mode Tabs */}
      <div className="flex gap-2 mb-4">
        <Button
          variant={viewMode === 'sessions' ? 'default' : 'outline'}
          onClick={() => setViewMode('sessions')}
          className={viewMode === 'sessions' ? 'bg-[#6d4f79] hover:bg-[#5a3f66]' : ''}
        >
          Session View
        </Button>
        <Button
          variant={viewMode === 'daily' ? 'default' : 'outline'}
          onClick={() => setViewMode('daily')}
          className={viewMode === 'daily' ? 'bg-[#6d4f79] hover:bg-[#5a3f66]' : ''}
        >
          Daily View
        </Button>
      </div>

      {/* Content based on view mode */}
      {viewMode === 'sessions' ? (
        /* Session View - Individual game sessions */
        <Card>
          <CardHeader>
            <CardTitle>Individual Game Sessions</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Loading analytics...</div>
            ) : (analytics && analytics.sessions && analytics.sessions.length > 0) ? (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Game</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Time</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead className="text-center">Reps</TableHead>
                      <TableHead className="text-center">Form Score</TableHead>
                      <TableHead className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          ROM
                          <div className="relative group">
                            <Info className="h-3 w-3 text-gray-400 cursor-help" />
                            <div className="absolute left-1/2 -translate-x-1/2 top-5 w-48 p-3 bg-white border-2 border-gray-300 text-gray-900 text-xs rounded shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                              <div className="font-bold mb-1.5 text-gray-800">ROM Ranges:</div>
                              <div className="text-gray-700">Full: ≥90% | ¾: 67-89%</div>
                              <div className="text-gray-700">½: 45-66% | ¼: 22-44%</div>
                              <div className="text-gray-700">Limited: &lt;22%</div>
                            </div>
                          </div>
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      // Pagination logic
                      const sessionsPerPage = 10;
                      const totalSessionPages = Math.ceil(analytics.sessions.length / sessionsPerPage);
                      const sessionsStartIndex = (sessionsPage - 1) * sessionsPerPage;
                      const sessionsEndIndex = sessionsStartIndex + sessionsPerPage;
                      const paginatedSessions = analytics.sessions.slice(sessionsStartIndex, sessionsEndIndex);
                      
                      return paginatedSessions.map((session, index) => (
                        <TableRow key={session.id || index} className="hover:bg-gray-50">
                          <TableCell className="font-medium">{session.game_name}</TableCell>
                          <TableCell className="text-sm">{formatDate(session.start_time)}</TableCell>
                          <TableCell className="text-sm text-gray-600">{formatTime(session.start_time)}</TableCell>
                          <TableCell>{formatDuration(session.duration_seconds)}</TableCell>
                          <TableCell className="text-center">{session.reps_completed || 0}</TableCell>
                          <TableCell className="text-center">
                            <span className={`font-semibold ${
                              session.form_score >= 85 ? 'text-green-600' :
                              session.form_score >= 70 ? 'text-amber-600' : 'text-red-600'
                            }`}>
                              {session.form_score?.toFixed(0) || 0}%
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              {session.rom_display || 'N/A'}
                            </span>
                          </TableCell>
                        </TableRow>
                      ));
                    })()}
                  </TableBody>
                </Table>
                
                {/* Pagination */}
                {analytics.sessions.length > 10 && (
                  <div className="flex justify-center mt-4">
                    <Pagination
                      count={Math.ceil(analytics.sessions.length / 10)}
                      page={sessionsPage}
                      onChange={(e, page) => setSessionsPage(page)}
                      color="primary"
                      size="small"
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                {isPlaceholderData ?
                  'Showing demo data - real sessions will appear here when completed' :
                  'No game sessions found for the selected period'}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        /* Daily View - Cumulative daily statistics */
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Daily Cumulative Statistics</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">Loading daily summaries...</div>
              ) : dailySummaries?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-center">Sessions</TableHead>
                      <TableHead className="text-center">Duration</TableHead>
                      <TableHead className="text-center">Games</TableHead>
                      <TableHead className="text-center">Total Reps</TableHead>
                      <TableHead className="text-center">Avg Form</TableHead>
                      <TableHead className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          Avg ROM
                          <div className="relative group">
                            <Info className="h-3 w-3 text-gray-400 cursor-help" />
                            <div className="absolute left-1/2 -translate-x-1/2 top-5 w-48 p-3 bg-white border-2 border-gray-300 text-gray-900 text-xs rounded shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                              <div className="font-bold mb-1.5 text-gray-800">ROM Ranges:</div>
                              <div className="text-gray-700">Full: ≥90% | ¾: 67-89%</div>
                              <div className="text-gray-700">½: 45-66% | ¼: 22-44%</div>
                              <div className="text-gray-700">Limited: &lt;22%</div>
                            </div>
                          </div>
                        </div>
                      </TableHead>
                      <TableHead className="text-center">Upper Body Reps</TableHead>
                      <TableHead className="text-center">Core Reps</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dailySummaries.map((day, index) => (
                      <TableRow key={day.id || index} className="hover:bg-gray-50">
                        <TableCell className="font-medium">{formatDate(day.summary_date)}</TableCell>
                        <TableCell className="text-center">{day.session_count}</TableCell>
                        <TableCell className="text-center">{formatDuration(day.total_platform_time_seconds)}</TableCell>
                        <TableCell className="text-center">{day.total_games_played}</TableCell>
                        <TableCell className="text-center">{day.total_reps}</TableCell>
                        <TableCell className="text-center">
                          <span className={`font-semibold ${
                            day.avg_form_score >= 85 ? 'text-green-600' :
                            day.avg_form_score >= 70 ? 'text-amber-600' : 'text-red-600'
                          }`}>
                            {day.avg_form_score?.toFixed(0) || 0}%
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            {day.avg_rom_percentage >= 90 ? 'Full' :
                             day.avg_rom_percentage >= 67 ? '¾' :
                             day.avg_rom_percentage >= 45 ? '½' :
                             day.avg_rom_percentage >= 22 ? '¼' : 'Limited'}
                          </span>
                        </TableCell>
                        <TableCell className="text-center text-sm">{day.upper_body_reps || 0}</TableCell>
                        <TableCell className="text-center text-sm">{day.core_reps || 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  {isPlaceholderData ?
                    'Showing demo data - real daily summaries will appear here when games are completed' :
                    'No daily summaries available for the selected period'}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Game Performance Summary */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Game Performance Analytics</CardTitle>
            {Object.keys(gamePerformance).length > 0 && (
              <select
                className="px-4 py-2 pr-10 border rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#6d4f79] appearance-none bg-no-repeat bg-right"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                  backgroundPosition: 'right 0.5rem center',
                  backgroundSize: '1.5em 1.5em'
                }}
                value={selectedGame || ''}
                onChange={(e) => setSelectedGame(e.target.value)}
              >
                <option value="">Select a game to view details</option>
                {Object.keys(gamePerformance).map(gameName => (
                  <option key={gameName} value={gameName}>{gameName}</option>
                ))}
              </select>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {Object.keys(gamePerformance).length > 0 ? (
            selectedGame && gamePerformance[selectedGame] ? (
              <div className="space-y-6">
                {/* Key Metrics for Selected Game */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
                    <p className="text-sm text-purple-700 mb-1">Sessions Played</p>
                    <p className="text-2xl font-bold text-purple-900">{gamePerformance[selectedGame].sessions}</p>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
                    <p className="text-sm text-green-700 mb-1">Avg Form Score</p>
                    <p className="text-2xl font-bold text-green-900">{gamePerformance[selectedGame].avgFormScore?.toFixed(0) || 0}%</p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                    <p className="text-sm text-blue-700 mb-1">Total Reps</p>
                    <p className="text-2xl font-bold text-blue-900">{gamePerformance[selectedGame].totalReps}</p>
                  </div>
                  <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-4">
                    <p className="text-sm text-amber-700 mb-1">Completion Rate</p>
                    <p className="text-2xl font-bold text-amber-900">
                      {gamePerformance[selectedGame].sessions > 0 ?
                        ((gamePerformance[selectedGame].completions / gamePerformance[selectedGame].sessions) * 100).toFixed(0) : 0}%
                    </p>
                  </div>
                </div>

                {/* Performance Trend Charts - Separated for Clarity */}
                {analytics?.sessions?.filter(s => s.game_name === selectedGame).length > 0 && (
                  <div className="grid grid-cols-2 gap-6">
                    {/* Form Score Chart */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold">Form Score Trend</h4>
                        <div className="group relative">
                          <button className="text-gray-400 hover:text-gray-600 focus:outline-none">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                          <div className="absolute right-0 w-64 p-3 bg-white border rounded-lg shadow-lg opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-10">
                            <p className="text-xs text-gray-600">
                              <strong>Form Score:</strong> Quality of exercise execution (0-100%). Higher scores indicate better form and technique.
                            </p>
                          </div>
                        </div>
                      </div>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={(() => {
                          // Get sessions for this game and sort chronologically
                          const gameSessions = analytics.sessions
                            .filter(s => s.game_name === selectedGame)
                            .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
                            .map(s => ({
                              date: formatDate(s.start_time),
                              time: formatTime(s.start_time),
                              formScore: s.form_score || 0
                            }));

                          return gameSessions;
                        })()} margin={{ left: 20, right: 20, top: 10, bottom: 60 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 11 }}
                            angle={-45}
                            textAnchor="end"
                            height={60}
                          />
                          <YAxis
                            tick={{ fontSize: 11 }}
                            ticks={[0, 20, 40, 60, 80, 100]}
                            label={{ value: 'Form Score (%)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#666', fontSize: 12, fontWeight: 500 } }}
                            domain={[0, 100]}
                          />
                          <Tooltip
                            contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e5e5', borderRadius: '8px' }}
                            formatter={(value) => [`${value.toFixed(1)}%`, 'Form Score']}
                          />
                          <Line
                            type="monotone"
                            dataKey="formScore"
                            stroke="#6d4f79"
                            strokeWidth={3}
                            dot={{ fill: '#6d4f79', r: 5 }}
                            activeDot={{ r: 8 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Repetitions Chart */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold">Repetitions Trend</h4>
                        <div className="group relative">
                          <button className="text-gray-400 hover:text-gray-600 focus:outline-none">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                          <div className="absolute right-0 w-64 p-3 bg-white border rounded-lg shadow-lg opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-10">
                            <p className="text-xs text-gray-600">
                              <strong>Repetitions:</strong> Number of completed exercise reps per session. Tracks patient volume and consistency.
                            </p>
                          </div>
                        </div>
                      </div>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={(() => {
                          // Get sessions for this game and sort chronologically
                          const gameSessions = analytics.sessions
                            .filter(s => s.game_name === selectedGame)
                            .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
                            .map(s => ({
                              date: formatDate(s.start_time),
                              time: formatTime(s.start_time),
                              reps: s.reps_completed || 0
                            }));

                          return gameSessions;
                        })()} margin={{ left: 20, right: 20, top: 10, bottom: 60 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 11 }}
                            angle={-45}
                            textAnchor="end"
                            height={60}
                          />
                          <YAxis
                            tick={{ fontSize: 11 }}
                            label={{ value: 'Repetitions', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#666', fontSize: 12, fontWeight: 500 } }}
                            domain={[0, 'auto']}
                          />
                          <Tooltip
                            contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e5e5', borderRadius: '8px' }}
                            formatter={(value) => [value, 'Repetitions']}
                          />
                          <Line
                            type="monotone"
                            dataKey="reps"
                            stroke="#10b981"
                            strokeWidth={3}
                            dot={{ fill: '#10b981', r: 5 }}
                            activeDot={{ r: 8 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

              </div>
            ) : (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                  <BarChart3 className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Select a Game to View Analytics</h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  Choose a game from the dropdown above to see detailed performance metrics, trends, and insights.
                </p>
              </div>
            )
          ) : (
            <div className="text-center py-8 text-gray-500">
              {isPlaceholderData ?
                'Showing demo performance data' :
                'No game performance data available'}
            </div>
          )}
        </CardContent>
      </Card>


      {/* Mendy's Weekly Insights Modal */}
      <Dialog open={showInsightsModal} onOpenChange={setShowInsightsModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto [&>button:last-child]:hidden">
          {/* Custom Copy Button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleCopyInsights}
            className="absolute right-20 top-4 h-10 w-10 rounded-sm opacity-70 hover:opacity-100 z-10"
            title={copySuccess ? "Copied!" : "Copy Insights"}
          >
            {copySuccess ? (
              <svg 
                width="25" 
                height="25" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="#10b981" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                style={{ width: '25px', height: '25px', minWidth: '25px', minHeight: '25px' }}
              >
                <path d="m9 12 2 2 4-4"/>
                <path d="M21 12c.552 0 1-.448 1-1s-.448-1-1-1-1 .448-1 1 .448 1 1 1"/>
                <path d="M17 7h-3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-3"/>
              </svg>
            ) : (
              <svg 
                width="25" 
                height="25" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="#9ca3af" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                style={{ width: '25px', height: '25px', minWidth: '25px', minHeight: '25px' }}
              >
                <rect width="14" height="14" x="8" y="8" rx="2" ry="2"/>
                <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>
              </svg>
            )}
          </Button>

          {/* Custom Close Button to match copy button styling */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowInsightsModal(false)}
            className="absolute right-4 top-4 h-10 w-10 rounded-sm opacity-70 hover:opacity-100 z-10"
            title="Close"
          >
            <svg 
              width="25" 
              height="25" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="#9ca3af" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              style={{ width: '25px', height: '25px', minWidth: '25px', minHeight: '25px' }}
            >
              <path d="M18 6 6 18"/>
              <path d="m6 6 12 12"/>
            </svg>
          </Button>

          <DialogHeader className="pr-24">
            <div className="flex items-center gap-3">
              <img
                src="/auth/logo.svg"
                alt="MoveMend"
                className="h-8 w-8"
              />
              <DialogTitle className="text-xl font-bold">Mendy's Weekly Insights</DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            {/* Patient Info */}
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm font-medium text-gray-700">
                Patient: <span className="font-semibold">
                  {selectedPatient?.firstName || selectedPatient?.firstname || 'Patient'} {selectedPatient?.lastInitial || selectedPatient?.lastinitial || ''}
                </span>
              </p>
              <p className="text-sm text-gray-600">Date: {new Date().toLocaleDateString()}</p>
            </div>

            {/* Combined Insights Box */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              {/* Overview */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">Overview</h4>
                <p className="text-sm text-gray-700">
                  Patient progressed well this week. Patient engaged consistently in supplemental exercises,
                  averaging 2 minutes more per session compared to the prior week. Notably, patient achieved
                  a new personal best in overhead reach (Apple Picking), completing 45 repetitions in 3 minutes.
                </p>
              </div>

              <div className="border-t pt-6 mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">Strengths</h4>
                <ul className="space-y-1 text-sm text-gray-700 list-disc list-inside">
                  <li>Daily engagement maintained throughout the week</li>
                  <li>Increased total exercise time (+10 minutes compared to last week)</li>
                  <li>Strong performance in upper body games, especially overhead reach</li>
                </ul>
              </div>

              <div className="border-t pt-6 mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">Areas to Watch</h4>
                <ul className="space-y-1 text-sm text-gray-700 list-disc list-inside">
                  <li>Average session duration is still below the assigned 20-minute target</li>
                  <li>Range of motion in upper extremity exercises shows improvement but remains variable</li>
                  <li>Sessions occurred at different times of day, which may affect consistency</li>
                </ul>
              </div>

              <div className="border-t pt-6">
                <h4 className="font-semibold text-gray-900 mb-3">Recommendations</h4>
                <p className="text-xs text-gray-500 italic mb-2">
                  (These suggestions are based solely on objective exercise data. Intended for clinician review.
                  Please apply your clinical judgment to determine appropriateness.)
                </p>
                <ul className="space-y-1 text-sm text-gray-700 list-disc list-inside">
                  <li>Consider upgrading program by adding a new game or increasing duration/frequency</li>
                  <li>Encourage consistent timing for sessions to support habit formation</li>
                </ul>
              </div>
            </div>
          </div>

        </DialogContent>
      </Dialog>

    </div>
  );
};

export default PatientAnalyticsView;